import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "../components/tabon/Logo";
import { api, formatError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success(`Welcome back, ${data.name}`);
      // Return the owner to wherever ProtectedRoute bounced them from — the EOD report deep link, say
      // — instead of always the dashboard (J-5d). Only honour an in-app path (one we pushed as state),
      // never an absolute/external URL, so this can't be used as an open redirect.
      const from = location.state?.from;
      const safePath = from && typeof from.pathname === "string"
        && from.pathname.startsWith("/") && !from.pathname.startsWith("//");
      const nextPath = safePath ? `${from.pathname}${from.search || ""}` : "/dashboard";
      navigate(nextPath, { replace: true });
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 tb-hero-gradient">
      <div className="absolute inset-0 tb-grid-bg opacity-40 pointer-events-none" />
      <div className="relative w-full max-w-md tb-fade-up">
        <div className="flex flex-col items-center mb-8">
          <Logo size="xl" />
          <p className="text-[#A0A0A0] mt-3 text-sm italic">
            Turn every visit into the next one.
          </p>
        </div>

        <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-7 shadow-2xl">
          <h1 className="text-2xl font-bold mb-1">Venue Admin Login</h1>
          <p className="text-sm text-[#A0A0A0] mb-6">Sign in to your venue dashboard</p>

          <form onSubmit={handleLogin} className="flex flex-col gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
                Email
              </label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white placeholder:text-[#6B6B6B] focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
                placeholder="you@venue.com"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
                Password
              </label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white placeholder:text-[#6B6B6B] focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40 pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0]"
                  data-testid="toggle-password-visibility"
                >
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 text-white font-semibold rounded-lg py-3 min-h-[44px] transition-all active:scale-[0.99]"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
            <div className="text-center">
              <Link to="/forgot-password" className="text-xs text-[#A0A0A0] hover:text-[#9D00FF] transition" data-testid="forgot-password-link">
                Forgot Password?
              </Link>
            </div>
          </form>

          <div className="text-center mt-5 space-y-1.5">
            <div className="text-sm text-[#A0A0A0]">
              New venue?{" "}
              <Link to="/signup" className="text-[#9D00FF] font-semibold hover:underline" data-testid="signup-link">
                Create account
              </Link>
            </div>
            <div className="text-sm text-[#A0A0A0]">
              Staff member?{" "}
              <Link to="/staff-login" className="text-[#9D00FF] font-semibold hover:underline" data-testid="staff-login-link">
                Login with PIN
              </Link>
            </div>
          </div>
        </div>

        <div className="text-center text-xs text-[#A0A0A0] mt-6">
          Powered by <span className="text-white">TabOn</span> · tabon.app
        </div>
      </div>
    </div>
  );
}
